import { type FC, Fragment, useEffect, useMemo, useState } from "react";

import {
  type AndOrCondition,
  ConditionType,
  type PropertyCondition,
  type TraitCondition,
} from "@hightouch/lib/query/visual/types";
import {
  Button,
  ButtonGroup,
  Checkbox,
  Column,
  Dialog,
  Row,
} from "@hightouch/htui";

import { ReadOnlyConditions } from "src/components/explore/read-only-conditions";
import { colors } from "src/components/explore/visual/colors";
import type { Condition } from "src/types/visual";
import { AndOrToggleButton } from "src/components/explore/visual/condition-buttons";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  traitConditions: TraitCondition[];
  onImport: (selected: TraitCondition[]) => void;
};

type SelectableRow = {
  id: string;
  tcIndex: number;
  condition: AndOrCondition<PropertyCondition>;
  parentType: ConditionType.And | ConditionType.Or;
};

export const ImportConditionsModal: FC<Readonly<Props>> = ({
  isOpen,
  onClose,
  traitConditions,
  onImport,
}) => {
  const rows = useMemo<SelectableRow[]>(
    () =>
      traitConditions.flatMap((tc, tcIndex) =>
        tc.conditions.map((condition, condIndex) => ({
          id: `${tcIndex}-${condIndex}`,
          tcIndex,
          condition,
          parentType: tc.type,
        })),
      ),
    [traitConditions],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
    }
  }, [isOpen]);

  const toggle = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleImport = () => {
    const selected = rows.filter((r) => selectedIds.has(r.id));

    if (selected.length === 1) {
      const row = selected[0]!;
      const { condition, parentType } = row;
      if (
        condition.type === ConditionType.And ||
        condition.type === ConditionType.Or
      ) {
        onImport([condition as unknown as TraitCondition]);
      } else {
        onImport([{ type: parentType, conditions: [condition] }]);
      }
      return;
    }

    // Group selected rows back by their parent TraitCondition, preserving order
    const grouped = new Map<number, SelectableRow[]>();
    for (const row of selected) {
      const group = grouped.get(row.tcIndex) ?? [];
      group.push(row);
      grouped.set(row.tcIndex, group);
    }

    const result: TraitCondition[] = [...grouped.entries()]
      .sort(([a], [b]) => a - b)
      .flatMap(([, groupRows]) => {
        const firstRow = groupRows[0];
        if (!firstRow) return [];
        return [
          {
            type: firstRow.parentType,
            conditions: groupRows.map((r) => r.condition),
          },
        ];
      });

    onImport(result);
  };

  return (
    <Dialog
      isOpen={isOpen}
      variant="info"
      width="3xl"
      title="Import conditions from audience"
      onClose={onClose}
      actions={
        <ButtonGroup>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            isDisabled={selectedIds.size === 0}
            onClick={handleImport}
          >
            Import selected
          </Button>
        </ButtonGroup>
      }
    >
      <Column gap={2}>
        <Checkbox
          mb={2}
          label="Select all"
          isChecked={selectedIds.size === rows.length}
          onChange={() => {
            setSelectedIds(
              selectedIds.size === rows.length
                ? new Set()
                : new Set(rows.map((r) => r.id)),
            );
          }}
        />

        {rows.map((row, index) => {
          const isSelected = selectedIds.has(row.id);
          const prevRow = rows[index - 1];
          const showBadge =
            prevRow !== undefined && prevRow.tcIndex === row.tcIndex;

          return (
            <Fragment key={row.id}>
              {showBadge && (
                <AndOrToggleButton
                  conditionType={row.parentType}
                  isDisplayOnly
                  onClick={() => {}}
                />
              )}
              <Row
                gap={2}
                p={4}
                border="1px solid"
                borderColor={isSelected ? "primary.base" : "base.border"}
                borderRadius="xl"
                borderLeftColor={colors.base[row.parentType]}
                borderLeftWidth={12}
                alignItems="center"
                cursor="pointer"
                opacity={isSelected ? 1 : 0.75}
                onClick={() => {
                  toggle(row.id, !isSelected);
                }}
              >
                <ReadOnlyConditions
                  rootConditions={[row.condition] as unknown as Condition[]}
                  audiences={[]}
                  relationships={[]}
                  traits={[]}
                  journeys={[]}
                  noBackgroundColor
                />
              </Row>
            </Fragment>
          );
        })}
      </Column>
    </Dialog>
  );
};
